import { HStack, Box, Center } from '@chakra-ui/react';
import { ButtonLink, Infobox, EmptyState, Button } from 'Atoms';
import { ErrorIllustration, Typography } from 'Tokens';
import React, { useEffect, useState } from 'react';
import { Loader } from 'Molecules';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { nhost } from 'utils/nhost';
import { LockedIcon } from 'Tokens/Icons/Function';
import { LoginForm, LoginPage } from './Login';
import { useTranslation } from 'utils/translation';

const validateTokenAndLogin = (compassToken: string) => {
  return nhost.functions
    .call<{
      status?: 'no-pat' | 'success';
      company?: { name: string; id: string };
      session?: { refreshToken: string };
      userEmail?: string;
    }>('validate-compass-token', { compassToken })
    .then((validationRes) => {
      const status = validationRes.res?.data?.status;
      const error = validationRes.error?.message.error;

      if (!!error) {
        return { error };
      }

      if (status === 'no-pat') {
        return {
          company: validationRes.res?.data?.company,
          userEmail: validationRes.res?.data?.userEmail,
        };
      }

      return {
        token: validationRes.res?.data?.session?.refreshToken,
        company: validationRes.res?.data?.company,
        userEmail: validationRes.res?.data?.userEmail,
      };
    });
};

export function CompassLogin() {
  const { t } = useTranslation('login');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [company, setCompany] = useState<{ id: string; name: string }>();
  const [userEmail, setUserEmail] = useState<string>();
  const [compassTokenError, setCompassTokenError] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);

  const compassToken = searchParams.get('token') ?? '';

  useEffect(() => {
    if (compassToken) {
      validateTokenAndLogin(compassToken).then(({ token, company, userEmail, error }) => {
        if (!token) {
          setCompany(company);
          setUserEmail(userEmail);
          setCompassTokenError(error);
          setIsLoading(false);
        }
        if (!!token) {
          if (!!company?.id) {
            window.location.href = `/${company.id}/?refreshToken=${token}`;
          } else {
            window.location.href = `/?refreshToken=${token}`;
          }
        }
      });
    }
  }, [compassToken]);

  const handleCreateCompassPAT = async () => {
    setIsLoading(true);
    const session = await nhost.functions.call<{ status: string }>('create-compass-pat');
    if (!!session.res?.data?.status && session.res?.data?.status == 'Success') {
      navigate(`/${company?.id}`);
    }
  };

  if (isLoading) {
    return (
      <Center h="100vh" w="100vw" bg="white" position="absolute" zIndex={9999} top={0} left={0}>
        <Loader isLoaded={!isLoading} />
      </Center>
    );
  }

  if ((!company || !userEmail) && !!compassTokenError) {
    return (
      <Box w="100%" h="100vh" p="16px">
        <EmptyState
          title={'Error logging in with Compass'}
          description={
            <>
              <Typography variant="bodyStrong">{compassTokenError}</Typography>
              <Typography variant="body">
                Please try again by going back to Compass, or log in manually if you already have a
                Celsia account.
              </Typography>
            </>
          }
          callToAction={{
            text: 'Log in with email/password',
            variant: 'primary',
            onClick: () => {
              navigate('/login');
            },
          }}
          icon={<ErrorIllustration boxSize="120px" />}
        />
      </Box>
    );
  }

  return (
    <Box height="100vh">
      <LoginPage heading={`Login to ${company?.name}`}>
        <Infobox
          status="info"
          size="sm"
          description="By logging in you accept to connect your Celsia and Compass accounts."
          closable={false}
          marginBottom="16px"
        />
        <LoginForm disableEmailField email={userEmail} onSuccess={() => handleCreateCompassPAT()} />
        <HStack></HStack>
        <ButtonLink to="/changePassword" variant="ghost" width="100%" leftIcon={<LockedIcon />}>
          {t('login:forgotPassword')}
        </ButtonLink>
      </LoginPage>
    </Box>
  );
}
